import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';

const ToastContext = createContext(null);

export const TOAST_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger'
};

const TOAST_TOKENS = {
  info: { accent: 'var(--status-info)', soft: 'var(--status-info-soft)' },
  success: { accent: 'var(--status-running)', soft: 'var(--status-running-soft)' },
  warning: { accent: 'var(--status-queued)', soft: 'var(--status-queued-soft)' },
  danger: { accent: 'var(--status-failed)', soft: 'var(--status-failed-soft)' }
};

const TOAST_ICONS = {
  info: FiIcons.FiInfo,
  success: FiIcons.FiCheckCircle,
  warning: FiIcons.FiAlertTriangle,
  danger: FiIcons.FiXCircle
};

const TOAST_ROLES = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  danger: 'alert'
};

const DEFAULT_DURATION = 4000;
const EXIT_ANIMATION_MS = 160;
let nextToastId = 0;

function ToastCard({ toast, onDismiss }) {
  const Icon = TOAST_ICONS[toast.type] || FiIcons.FiInfo;
  const tokens = TOAST_TOKENS[toast.type] || TOAST_TOKENS.info;
  const leaving = toast.leaving === true;

  const styles = {
    toast: {
      position: 'relative',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      width: 344,
      padding: '14px 16px',
      borderRadius: 12,
      border: `1px solid ${tokens.accent}`,
      background: 'var(--surface-overlay)',
      boxShadow: 'var(--shadow-raised)',
      pointerEvents: 'auto',
      opacity: leaving ? 0 : 1,
      transform: leaving ? 'translateY(8px)' : 'translateY(0)',
      transition: `opacity ${EXIT_ANIMATION_MS}ms ease, transform ${EXIT_ANIMATION_MS}ms ease`,
      overflow: 'hidden'
    },
    icon: {
      flexShrink: 0,
      display: 'flex',
      marginTop: 2,
      padding: 4,
      borderRadius: 8,
      background: tokens.soft,
      color: tokens.accent
    },
    body: {
      flex: 1,
      minWidth: 0,
      display: 'grid',
      gap: 2
    },
    title: {
      color: 'var(--text-primary-ops)',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.35
    },
    message: {
      color: 'var(--text-secondary-ops)',
      fontSize: 12,
      lineHeight: 1.45
    },
    close: {
      flexShrink: 0,
      display: 'flex',
      padding: 2,
      border: 'none',
      background: 'transparent',
      borderRadius: 6,
      color: 'var(--text-secondary-ops)',
      lineHeight: 0
    }
  };

  return (
    <div className="toast-slide-in" role={TOAST_ROLES[toast.type] || 'status'} style={styles.toast}>
      <span style={styles.icon} aria-hidden="true"><Icon size={17} /></span>
      <div style={styles.body}>
        <span style={styles.title}>{toast.title}</span>
        {toast.message ? <span style={styles.message}>{toast.message}</span> : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        style={styles.close}
        onClick={() => onDismiss(toast.id)}
      >
        <FiIcons.FiX size={14} />
      </button>
    </div>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 10,
        pointerEvents: 'none'
      }}
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const beginExit = useCallback((id) => {
    setToasts((current) => current.map((toast) => (
      toast.id === id ? { ...toast, leaving: true } : toast
    )));
    window.setTimeout(() => removeToast(id), EXIT_ANIMATION_MS);
  }, [removeToast]);

  const dismissToast = useCallback((id) => {
    beginExit(id);
  }, [beginExit]);

  const showToast = useCallback((options) => {
    const { type = TOAST_TYPES.INFO, title = '', message = '', duration = DEFAULT_DURATION } = options || {};
    const id = nextToastId;
    nextToastId += 1;
    const toast = { id, type, title, message, duration, leaving: false };

    setToasts((current) => [...current, toast]);

    if (duration > 0) {
      window.setTimeout(() => beginExit(id), duration);
    }

    return id;
  }, [beginExit]);

  const contextValue = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}

export default ToastProvider;
